/**
 * Client-side defense for user-supplied URLs and embed HTML.
 *
 * Why this exists:
 *   - rel="noopener noreferrer" only governs *navigations*. A `javascript:`
 *     URL still executes script in the current origin when clicked, and a
 *     `data:` URL still loads attacker-controlled content. So we have to
 *     filter the scheme *before* we render the href.
 *   - The backend already validates Resource.url / embed_html (see
 *     backend/routes/resources.py — `_validate_safe_url` and
 *     `_sanitize_embed_html`). This module is the second layer: anything
 *     that lands in the DB before the validator existed, or any field we
 *     forget to validate on the server, gets caught here on render.
 *
 * The pair (server filter + client filter) is intentional. Defense-in-depth.
 */
import DOMPurify from "dompurify";
const SAFE_HREF_RE = /^(https?:|mailto:)/i;
/**
 * Returns a navigation-safe href, or "#" for anything that looks unsafe.
 *
 *   - "https://..." / "http://..." / "mailto:..." → passed through
 *   - "/relative/path" or "#anchor" → passed through (same-origin)
 *   - "javascript:alert(1)", "data:text/html,...", "vbscript:..." → "#"
 *   - empty / null / undefined → "#"
 *
 * Use everywhere we render a URL that originated from user input — even
 * if the field is "admin-only", because an admin account compromise is
 * the exact scenario where the second layer matters.
 */
export function safeHref(raw) {
    if (!raw)
        return "#";
    const trimmed = raw.trim();
    if (!trimmed)
        return "#";
    // Same-origin relative URLs are always fine — no scheme means no
    // pseudo-protocol attack surface.
    if (trimmed.startsWith("/") || trimmed.startsWith("#"))
        return trimmed;
    if (SAFE_HREF_RE.test(trimmed))
        return trimmed;
    return "#";
}
/**
 * Sanitize a snippet of embed HTML before passing it to
 * dangerouslySetInnerHTML. We only allow <iframe> with a tight attribute
 * allowlist — anything else (script, img, on* handlers, javascript: srcs)
 * gets stripped by DOMPurify.
 *
 * The backend uses bleach with the same allowlist; this is the mirror so a
 * pre-validator row in the DB can't pop a script in someone's browser.
 */
export function sanitizeEmbedHtml(raw) {
    if (!raw)
        return "";
    return DOMPurify.sanitize(raw, {
        ALLOWED_TAGS: ["iframe"],
        ALLOWED_ATTR: [
            "src",
            "width",
            "height",
            "title",
            "loading",
            "referrerpolicy",
            "allow",
            "sandbox",
            "frameborder",
            "allowfullscreen",
        ],
        // Only http/https for the iframe src — no javascript: / data: / file:
        ALLOWED_URI_REGEXP: /^https?:\/\//i,
    });
}
